import { Location } from '@angular/common';
import { ApplicationRef, Injectable, OnInit, Type, ViewContainerRef } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationExtras, NavigationStart, Params, Route, Router } from '@angular/router';
import { HashMap, TranslocoService } from '@jsverse/transloco';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MenuItem, MessageService } from 'primeng/api';
import { Observer, firstValueFrom, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { InsertionPointDirective } from 'src/modules/sm-base/directives/insertion-point.directive';
import { SmErrorHandler } from 'src/modules/sm-base/misc/sm-error-handler';
import { ButtonDefinition } from 'src/modules/sm-base/models/button-definition.model';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { PrimeMessageSeverity } from 'src/modules/sm-base/models/prime-message-severity.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { AuthenticationService } from 'src/modules/sm-base/services/authentication.service';
import { FeatureModuleService } from 'src/modules/sm-base/services/feature-module.service';
import { MessageDialogService } from 'src/modules/sm-base/services/message-dialog.service';
import { AuthRules } from 'src/modules/sm-base/shared/auth-rules.model';
import { BackendResponse } from 'src/modules/sm-base/shared/backend-response.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Map2 } from 'src/modules/utils/shared/map2.model';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { UserInteractionItem } from 'src/modules/utils/shared/user-interaction-item.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { AppMainComponent } from '../components/app-main/app-main.component';
import { AppTab } from '../models/app-tab.model';
import { ComponentView } from '../models/component-view.model';
import { Config } from '../models/config.model';
import { PrimeModule } from 'src/modules/prime/prime.module';
import { SmTable2Component } from 'src/modules/sm-base/components/sm-table2/sm-table2.component';
import { MainFrontendConfig } from '../models/main-frontend-config.model';
import { SaveDataHandler } from '../models/save-data-handler.model';

@Injectable({
    providedIn: 'root'
})
export class MainAppService {

    //Das bentötigen wir, um aus AuthInterceptor auf den MainAppService zugreifen zu können - andernfalls gibt es bei der Initialisierung einen Dependency Injection Error
    static instance: MainAppService;

    appName: string;
    pageTitle: string;
    menuModel: AppTab[] = [];
    visibleMenuTabs: AppTab[] = [];
    language = "de";

    breadcrumbsVisible = false;
    breadcrumbsInToolBar = true;
    breadcrumbsHome: MenuItem = null;
    breadcrumbsItems: MenuItem[] = [];
    staticHeaderHtml: string;
    showSidebar = true;
    showTopbar = true;
    showSpinner = false;
    storedErrors: any[] = [];
    unreadNotificationCount = 0;
    debugRoutes = false;
    userInteractionHistory: UserInteractionItem[] = [];
    backendVersion = "";
    frontendVersionFromBackend = "";
    loadedDate: Date;
    lastUserInteractionDate: Date;
    mouseX = 0;
    mouseY = 0;
    advancedDeveloper = false;
    
    toolbarBackButton = false;
    useNotifications = false;
    toolbarHomeButton = false;
    toolbarNotificationsButton = false;
    toolbarHelpButton = false;
    toolbarButtons: MenuItem[] = [];
    toolbarShowUser = true;
    multilingual = false;

    contextMenu: MenuItem[];
    contextMenuClass = "";

    changeDetectionCount = 0;

    mainConfig: MainFrontendConfig = null;

    private keyboardListeners: OrdinaryObject<(KeyboardEvent) => void> = {};
    private keyboardListenersByKey: OrdinaryObject<string[]> = {};

    onSelectionCallback: () => void;

    constructor(
        private titleService: Title,
        private router: Router,
        private location: Location,
        public messageService: MessageService,
        public messageDialog: MessageDialogService,
        private transloco: TranslocoService,
        private applicationRef: ApplicationRef,
        public sanitizer: DomSanitizer,
        public auth: AuthenticationService,
        public featureModules: FeatureModuleService,
        public deviceDetector: DeviceDetectorService) {

        GuiUtils.checkSingleton(this);
        MainAppService.instance = this;
        this.updatePageTitle(Config.get().pageTitle);
        this.showSidebar = Config.get().showSidebar;
        this.toolbarBackButton = Config.get().showBackButton;
        this.toolbarHelpButton = Config.get().showHelpButton;
        this.toolbarShowUser = !Config.get().allowAnonymous;
        this.breadcrumbsInToolBar = Config.get().breadcrumbsInToolBar;
        this.staticHeaderHtml = Config.get().staticHeaderHtml;
        this.loadedDate = new Date();
        this.language = localStorage.getItem("language");
        //PRAGMATIC (zukünftig konfigurierbar)
        if (this.language != "de" && this.language != "en" && this.language != "fr" && this.language != "es") {
            this.language = "de";
        }
        this.setLanguage(null).catch(() => {});
        this.multilingual = Config.get().multilingual;

        if (Config.get().developer) {
            SmErrorHandler.instance.onError.push(error => this.storedErrors.push(error));
        }

        if (Config.get().backendV2) {
            SmTable2Component.convertCsvToExcel = async csv => RestEndpoint.main().body({ text: csv }).post().run("api/app/csvToExcel").getString();
            SmTable2Component.convertCsvToExcel = async (csv, columns) => RestEndpoint.main().body({ text: csv, columns }).post().run("api/app/csvToExcel").getString();
    }

        window.addEventListener('unhandledrejection', (event) => {
            SmErrorHandler.instance.handleError(event.reason);
        });

        this.router.events.subscribe(event => {
            if (this.debugRoutes) {
                console.group(`Router event: ${event.constructor.name}`);
                console.log(event);
                console.groupEnd();
            }
            if (event instanceof NavigationStart) {
                this.userInteractionHistory.push(new UserInteractionItem(new Date(), "URL", [event.url]));
            }
        });

        (window as any).smDebug = {
            getClassesWithoutStyles: () => {
                const allElements = document.querySelectorAll('*');
                const classWithoutStyles = new Map2<string, number>();

                allElements.forEach(element => {
                    if (typeof element.className === "string") {
                        const classes = element.className.split(/\s+/);
                        classes.forEach(className => {
                            const style = getComputedStyle(element, '.' + className);
                            // Überprüft, ob die resultierenden Stile nur Standardwerte haben
                            if (className != '' && (!style || style.length === 0 || style.cssText === '')) {
                                classWithoutStyles.set(className, classWithoutStyles.getSafe(className, 0) + 1);
                            }
                        });
                    }
                });
                console.log(classWithoutStyles.itemsToString("\n"));
            }
        };

        timer(1_000, 60_000 * 5).subscribe(async _ => {
            await this.checkNotifications();
        });
    }

    updateMenuVisibility(): void {
        this.visibleMenuTabs = this.menuModel.filter(t => t.visible);
    }

    async setLanguage(value: string): Promise<void> {
        if (value == null || value != this.language) {
            let lng = value ?? this.language;
            this.transloco.setActiveLang(lng);
            await firstValueFrom(this.transloco.load(lng));
            PrimeModule.instance.setLanguage(lng);
            SmTable2Component.setLanguage(lng);
        }
        if (value != null) {
            this.language = value;
            localStorage.setItem("language", value);
        }
    }    

    async checkNotifications(): Promise<void> {
        if (this.auth.isLoggedIn() && this.useNotifications) {
            this.unreadNotificationCount = await RestEndpoint.main().run("api/app/notification/unreadcount").getNumber();
        }
    }

    getUserName(): string {
        return this.auth.getUserName();
    }

    userInteracted(): void {
        this.lastUserInteractionDate = new Date();
    }

    userKeyPress(event: KeyboardEvent): string {
        this.userInteracted();

        let key = event.key;
        if (key == null) {
            return null;
        }

        if (event.altKey) {
            key = "Alt-" + key;
        }
        if (event.ctrlKey) {
            key = "Ctrl-" + key;
        }
        if (event.shiftKey) {
            key = "Shift-" + key;
        }
        key = key.toUpperCase();
        for (let guid of Utils.getSafe(this.keyboardListenersByKey, key, [])) {
            this.keyboardListeners[guid](event);
        }
        return key;
    }

    addKeyboardListener(key: string, callback: (KeyboardEvent) => void): string {
        key = key.toUpperCase();
        let guid = Utils.generateGuid();
        this.keyboardListeners[guid] = callback;
        if (!(key in this.keyboardListenersByKey)) {
            this.keyboardListenersByKey[key] = [];
        }
        this.keyboardListenersByKey[key] = [...this.keyboardListenersByKey[key], guid];
        return guid;
    }

    removeKeyboardListener(guid: string): void {
        delete this.keyboardListeners[guid];
        for (let key of Utils.getOwnPropertyNames(this.keyboardListenersByKey)) {
            this.keyboardListenersByKey[key] = this.keyboardListenersByKey[key].filter(s => s != guid);
        }
    }

    //callback eigentlich vom Typ callback: (params: any) => any, das kollidiert aber mit eslint rule so-unsafe-argument
    subscribeToParamsChanges(route: ActivatedRoute, callback: any): void {
        route.params.subscribe(callback as Partial<Observer<Params>>);
    }

    //callback eigentlich vom Typ callback: (params: any) => any, das kollidiert aber mit eslint rule so-unsafe-argument
    subscribeToQueryParamsChanges(route: ActivatedRoute, callback: any): void {
        route.queryParams.subscribe(callback as Partial<Observer<Params>>);
    }

    backendVersionRetrieved(backendVersion: string, frontendVersionFromBackend: string): void {
        if (!Utils.isNoe(backendVersion)) {
            this.backendVersion = backendVersion;
        }
        if (!Utils.isNoe(frontendVersionFromBackend)) {
            this.frontendVersionFromBackend = frontendVersionFromBackend;
        }
    }

    async navigate(commands: any[], extras?: NavigationExtras): Promise<void> {
        await this.router.navigate(commands, extras);
    }

    navigateBack(): void {
        this.location.back();
    }

    reloadPage(): void {
        window.location.reload();
    }

    async navigateToUrl(url: string): Promise<void> {
        await this.router.navigateByUrl(url);
    }

    async navigateTo(routerLink: any[], replaceUrl?: boolean): Promise<void> {
        await this.router.navigate(routerLink, { replaceUrl });
    }

    updateNavigation(pageTitle: string, breadcrumbsItems: MenuItem[]): void {
        GuiUtils.angularTimer(() => {
            this.updatePageTitle(pageTitle || (breadcrumbsItems.length == 0 ? "Home" : breadcrumbsItems[breadcrumbsItems.length - 1].label));
            this.updateBreadcrumbs(breadcrumbsItems);
        });
    }

    updateBreadcrumbs(breadcrumbsItems: MenuItem[]): void {
        this.breadcrumbsHome = { routerLink: ["/"], icon: 'fas fa-home' };
        this.breadcrumbsItems = Utils.arrayWithoutNull(breadcrumbsItems);
        if (this.breadcrumbsItems.length > 0) {
            delete this.breadcrumbsItems[this.breadcrumbsItems.length - 1].routerLink;
        }
        this.breadcrumbsVisible = Config.get().showBreadcrumbs;
        if (this.breadcrumbsItems.length == 0) {
            this.breadcrumbsItems = null;
        }
    }

    updatePageTitle(pageTitle: string): void {
        this.pageTitle = pageTitle;
        this.titleService.setTitle(pageTitle);
    }

    async updateQueryParams(key: string, value: any): Promise<void> {
        let queryParams: OrdinaryObject = {};
        queryParams[key] = Utils.toString(value);
        await this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
    }

    getEnvironment(): any {
        return environment;
    }

    getInstance(): this {
        return this;
    }

    createComponent<T>(parent: ViewContainerRef | InsertionPointDirective, type: Type<T>): T {
        let viewContainerRef: ViewContainerRef = parent instanceof InsertionPointDirective ? parent.viewContainerRef : parent;
        viewContainerRef.clear();
        return viewContainerRef.createComponent(type).instance;
    }

    showToast(severity: PrimeMessageSeverity, summary: string, detail: string, duration?: number): void {
        this.messageService.add({ severity, summary, detail, life: duration });
    }

    changeTheme(theme: string): void {
        this.replaceLink(document.getElementById('theme-css') as HTMLLinkElement, theme);
    }

    changeLayout(layout: string): void {
        this.replaceLink(document.getElementById('layout-css') as HTMLLinkElement, layout);
    }

    async copyToClipboardOrDialog(text: string, dialogTitle: string, copyToastText: string): Promise<void> {
        if (await GuiUtils.copyToClipboard(text)) {
            this.showToast("success", "Kopiert", copyToastText);
        }
        else {
            this.messageDialog.textArea(dialogTitle, text);
        }
    }

    isAdmin(): boolean {
        return this.auth.userDetails?.getDatabaseUser()?.isAdmin ?? false;
    }

    replaceLink(linkElement: HTMLLinkElement, href: string): void {
        const id = linkElement.getAttribute('id');
        const cloneLinkElement = linkElement.cloneNode(true) as HTMLLinkElement;

        cloneLinkElement.setAttribute('href', href);
        cloneLinkElement.setAttribute('id', id + '-clone');

        linkElement.parentNode.insertBefore(cloneLinkElement, linkElement.nextSibling);

        cloneLinkElement.addEventListener('load', () => {
            linkElement.remove();
            cloneLinkElement.setAttribute('id', id);
        });
    }

    getRouterDebugString(): string {
        return this.getRouterDebugStringInternal('', this.router.config);
    }

    triggerChangeDetection(): void {
        this.applicationRef.tick();
    }

    validateForm(form: FrontendFormDefinition, additionalValidation: () => OrdinaryObject = null): boolean {
        let result = form.validate();
        if (additionalValidation != null) {
            result = { ...result, ...additionalValidation()};
        }
        if (result != null && Utils.getOwnPropertyNames(result).length > 0) {
            this.showToast("error", "Fehler in der Eingabe", Utils.arrayItemsToString(Utils.objectGetValues(result), "\n\n"));
            return false;
        }
        return true;
    }

    async genericDataHandler<T>(worker: () => Promise<T>, errorTitle?: string, onError?: "toast" | "dialog" | null): Promise<{ok: boolean, value: T}> {
        try {
            return {ok: true, value: await worker()};
        }
        catch (ex) {
            if (onError != null) {
                let error = BackendResponse.getFromException(ex);
                if (onError == "dialog") {
                    await this.messageDialog.info(!Utils.isNoe(error.items) ? error.items[0].message : errorTitle, errorTitle);
                }
                else if (onError == "toast") {
                    this.showToast("error", errorTitle, !Utils.isNoe(error.items) ? error.items[0].message : errorTitle);
                }
            }
            return {ok: false, value: null};
        }
    }

    async loadDataHandler<T>(worker: () => Promise<T>, source: ComponentView): Promise<T> {
        try {
            return await worker();
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            await this.loadDataError(!Utils.isNoe(error?.items) ? error.items[0].message : null, !Utils.isNoe(error?.items) && error.items[0].recoverable, source);
            throw ex;
        }
    }

    async loadDataError(message: string, recoverable: boolean, source: ComponentView): Promise<void> {
        let buttons = [new ButtonDefinition("back", "Zurück", false, "fas fa-backward")];
        if (recoverable) {
            buttons = [new ButtonDefinition("reload", "Erneut versuchen", false, "fas fa-redo-alt"), ...buttons];
        }
        let result = await this.messageDialog.show(message, "Fehler beim Laden der Daten", buttons);
        if (result == "reload") {
            source.ngOnInit();
        }
        else if (result == "back") {
            this.navigateBack();
        }
    }

    async saveDataHandler(worker: () => Promise<any>, componentView?: ComponentView, successToast?: boolean, navigateBack?: boolean, successTitle?: string, successText?: string): Promise<[boolean, any]> {
        try {
            let result = await worker();

            if (successToast) {
                this.showToast("success", successTitle ?? this.t("general.updatedTitle"), successText ?? this.t("general.updatedText"));
            }
            if (navigateBack) {
                if (componentView != null) {
                    componentView.skipCloseCheck = true;
                }
                this.navigateBack();
            }

            return [true, result];
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            await this.messageDialog.info(!Utils.isNoe(error.items) ? error.items[0].message : "Es trat ein Fehler beim Speichern auf.", "Fehler beim Speichern");
            return [false, null];
        }
    }

    async saveDataHandler2(data: SaveDataHandler): Promise<[boolean, any]> {
        try {
            let result = await data.worker();

            if (data.successToast) {
                this.showToast("success", data.successTitle ?? this.t("general.updatedTitle"), data.successText ?? this.t("general.updatedText"));
            }
            if (data.navigateBack) {
                if (data.componentView != null) {
                    data.componentView.skipCloseCheck = true;
                }
                this.navigateBack();
            }
            else if (data.navigateTo != null) {
                if (data.componentView != null) {
                    data.componentView.skipCloseCheck = true;
                }
                this.navigateTo(data.navigateTo);
            }

            return [true, result];
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            await this.messageDialog.info(!Utils.isNoe(error.items) ? error.items[0].message : "Es trat ein Fehler beim Speichern auf.", "Fehler beim Speichern");
            return [false, null];
        }
    }

    async deleteDataHandler<T>(worker: () => Promise<T>, componentView?: ComponentView, successToast?: boolean, navigateBack?: boolean, successTitle?: string, successText?: string): Promise<[boolean, T]> {
        try {
            let result = await worker();

            if (successToast) {
                this.showToast("success", successTitle ?? this.t("general.deletedTitle"), successText ?? this.t("general.deletedText"));
            }
            if (navigateBack) {
                if (componentView != null) {
                    componentView.skipCloseCheck = true;
                }
                this.navigateBack();
            }

            return [true, result];
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            await this.messageDialog.info(!Utils.isNoe(error.items) ? error.items[0].message : "Es trat ein Fehler beim Löschen auf.", "Fehler beim Löschen");
            return [false, null];
        }
    }

    updateSuccess(): void {
        this.showToast("success", this.t("general.updatedTitle"), this.t("general.updatedText"));
        this.navigateBack();
    }

    public t(key: string, params?: HashMap, def?: string): string {
        let result = this.transloco.translateObject(key, params);
        return result == key && def != null ? def : result;
    }

    public tRegex(key: string, value: string): string {
        let result = this.transloco.translateObject(key);
        if (Utils.isArray(result)) {
            for (let i = 0; i < result.length; i += 2) {
                value = Utils.replaceAll(value, result[i], result[i + 1]);
            }
        }
        return value;
    }

    public tRegexHas(key: string): boolean {
        return Utils.isArray(this.transloco.translateObject(key));
    }

    public tCrudTitle(isNew: boolean, item: any, entityPath: string): string {
        let gender = this.t(entityPath + ".typeNameGender") ?? "m";
        gender = gender == "f" ? "e" : gender == "n" ? "es" : "er";
        return isNew ? this.t("crud.new") + gender + " " + this.t(entityPath + ".typeName") : this.t(entityPath + ".typeName") + " " + Utils.toString(item);
    }

    public tForm(entityPath: string, form: FrontendFormDefinition): FrontendFormDefinition {
        for (let field of form.fields) {
            if (Utils.isNoe(field.caption)) {
                field.caption = this.t(entityPath + "." + field.id);
            }
        }
        return form;
    }

    public tTable(entityPath: string, table: TableData): TableData {
        for (let col of table.columns) {
            if (Utils.isNoe(col.caption)) {
                col.caption = this.t(entityPath + "." + col.id);
            }
        }
        return table;
    }

    public getRouteParam(paramName: string): any {
        return this.getRouteParamInternal(this.router.routerState.snapshot.root, paramName);
    }

    private getRouteParamInternal(snapshot: ActivatedRouteSnapshot, paramName: string): any {
        if (Utils.hasProperty(snapshot.params, paramName)) {
            return snapshot.params[paramName];
        }
        else {
            for (let child of snapshot.children) {
                let result = this.getRouteParamInternal(child, paramName);
                if (result != null) {
                    return result;
                }
            }
        }
        return null;
    }

    private getRouterDebugStringInternal(parent: String, config: Route[], level = 0): string {
        let result = "";
        let prefix = " ".repeat(level * 2);
        for (let route of config) {
            result += prefix + parent + '/' + route.path + "\n";
            if (route.children) {
                const currentPath = route.path ? parent + '/' + route.path : parent;
                result += prefix + this.getRouterDebugStringInternal(currentPath, route.children, level + 1) + "\n";
            }
        }
        return result;
    }

    public isMobile(): boolean {
        return this.deviceDetector.isMobile();
    }

    openContextMenu(menu: MenuItem[], styleClass?: string): void {
        this.contextMenu = Utils.arrayWithoutNull(menu);
        if (styleClass != null) {
            this.contextMenuClass = styleClass;
        }
        GuiUtils.angularTimer(() => {
            AppMainComponent.instance.cm.show(this.getCurrentMousePosEvent());
        });
    }

    getCurrentMousePosEvent(): OrdinaryObject {
        let result: OrdinaryObject = {};
        result.pageX = this.mouseX;
        result.pageY = this.mouseY;
        result.stopPropagation = () => null;
        result.preventDefault = () => null;
        return result;
    }

}
